import { useState, useMemo, useEffect } from 'react';
import Papa from 'papaparse';
import { Download } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { Badge } from '@/components/ui/Badge';
import { Dropzone } from '@/components/ui/Dropzone';
import { Textarea } from '@/components/ui/Input';
import { useApp } from '@/store/app';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { readFileAsText } from '@/lib/utils';

const SAMPLE = `email,name
hi@example.com,Aarav Sharma
priya@example.com,Priya Mehta
hi@example.com,Aarav Sharma
HI@EXAMPLE.COM,Aarav Sharma
rohan@example.com,Rohan Iyer`;

export function CsvDedupe() {
  const { pendingFile, setPendingFile } = useApp();
  const [raw, setRaw] = useState(SAMPLE);
  const [keys, setKeys] = useState<string[]>([]);
  const [caseInsensitive, setCaseInsensitive] = useState(true);
  const [trim, setTrim] = useState(true);

  useEffect(() => {
    if (pendingFile && (pendingFile.type === 'text/csv' || pendingFile.name.endsWith('.csv'))) {
      readFileAsText(pendingFile).then(setRaw);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parsed = useMemo(() => {
    if (!raw.trim()) return { headers: [] as string[], rows: [] as Record<string, string>[] };
    const r = Papa.parse<Record<string, string>>(raw, { header: true, skipEmptyLines: true });
    return {
      headers: (r.meta.fields ?? []) as string[],
      rows: r.data,
    };
  }, [raw]);

  // Default keys to all columns once we know headers
  useEffect(() => {
    if (parsed.headers.length > 0 && keys.length === 0) {
      setKeys(parsed.headers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed.headers]);

  const result = useMemo(() => {
    const seen = new Set<string>();
    const dedup: Record<string, string>[] = [];
    const dups: Record<string, string>[] = [];
    for (const row of parsed.rows) {
      const key = keys
        .map((k) => {
          let v = row[k] ?? '';
          if (trim) v = v.trim();
          if (caseInsensitive) v = v.toLowerCase();
          return v;
        })
        .join('||');
      if (seen.has(key)) {
        dups.push(row);
      } else {
        seen.add(key);
        dedup.push(row);
      }
    }
    return { dedup, dups };
  }, [parsed, keys, caseInsensitive, trim]);

  function exportCsv() {
    const csv = Papa.unparse(result.dedup);
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'deduped.csv');
    toast.success('Saved');
  }

  function toggleKey(k: string) {
    setKeys((arr) => (arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]));
  }

  return (
    <div>
      <ToolHeader
        toolId="csv-dedupe"
        title="CSV deduplicator"
        description="Remove duplicate rows by selected columns. Configurable case-sensitivity and whitespace handling."
        badges={
          <>
            <Badge variant="success">{result.dedup.length} unique</Badge>
            <Badge variant="warning">{result.dups.length} duplicates</Badge>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:order-2 space-y-3">
          <div className="text-xs font-medium text-fg-muted">Match columns</div>
          <div className="space-y-1.5">
            {parsed.headers.map((h) => (
              <label key={h} className="flex items-center justify-between text-sm cursor-pointer">
                <span className="font-mono text-fg-muted">{h}</span>
                <Switch checked={keys.includes(h)} onChange={() => toggleKey(h)} />
              </label>
            ))}
            {parsed.headers.length === 0 && (
              <div className="text-2xs text-fg-subtle">Add CSV with a header row.</div>
            )}
          </div>
          <div className="border-t border-border pt-3 space-y-2.5">
            <label className="flex items-center justify-between text-sm cursor-pointer">
              <span className="text-fg-muted">Case-insensitive</span>
              <Switch checked={caseInsensitive} onChange={setCaseInsensitive} />
            </label>
            <label className="flex items-center justify-between text-sm cursor-pointer">
              <span className="text-fg-muted">Trim whitespace</span>
              <Switch checked={trim} onChange={setTrim} />
            </label>
          </div>
          <Button variant="primary" className="w-full" onClick={exportCsv}>
            <Download size={12} />
            Export deduped
          </Button>
        </Card>

        <div className="lg:col-span-2 lg:order-1 space-y-3">
          <Card className="!p-0">
            <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
              <span className="text-xs font-medium text-fg-muted">CSV with header</span>
              <Dropzone
                accept=".csv,text/csv"
                onFiles={async (f) => setRaw(await readFileAsText(f[0]))}
                hint=""
                className="!hidden"
              />
            </div>
            <Textarea
              mono
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              className="!border-0 !rounded-t-none min-h-[180px] !bg-transparent focus-visible:!ring-0"
            />
          </Card>
          {result.dups.length > 0 && (
            <Card>
              <div className="text-xs font-medium text-fg-muted mb-2">
                {result.dups.length} duplicate {result.dups.length === 1 ? 'row' : 'rows'}
              </div>
              <div className="overflow-auto max-h-72">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      {parsed.headers.map((h) => (
                        <th
                          key={h}
                          className="text-left px-3 py-2 text-2xs uppercase tracking-wider text-fg-subtle"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.dups.slice(0, 50).map((r, i) => (
                      <tr key={i} className="text-warning bg-warning/5">
                        {parsed.headers.map((h) => (
                          <td key={h} className="px-3 py-1.5 whitespace-nowrap">
                            {r[h]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export default CsvDedupe;
